<script setup lang="ts">
import { Form, Head, Link, usePage } from "@inertiajs/vue3";

import { Button } from "@/components/ui/button/index";
import SettingsLayout from "@/layouts/settings/layout";
import { identityEmailVerificationPath, settingsEmailPath } from "@/routes";

defineOptions({ inheritAttrs: false });
const page = usePage<{
  auth?: { user?: { email?: string; verified?: boolean } };
}>();
</script>

<template>
  <Head title="Email settings" />

  <main
    class="relative flex h-full min-h-0 w-full flex-col gap-3 bg-muted/50 p-3"
  >
    <SettingsLayout
      current="email"
      title="Email settings"
      description="Update and verify your email address."
    >
      <Form
        :action="settingsEmailPath()"
        method="patch"
        :reset-on-success="['password_challenge']"
        #default="{ errors, processing, recentlySuccessful }"
      >
        <div class="space-y-4">
          <div class="space-y-1.5">
            <label for="email-address" class="text-sm font-medium"
              >Email address</label
            >
            <input
              id="email-address"
              name="email"
              type="email"
              required
              :value="page.props.auth?.user?.email || ''"
              autocomplete="username"
              inputmode="email"
              class="input-field"
            />
            <p v-if="errors.email" class="text-sm text-destructive">
              {{ errors.email }}
            </p>
          </div>

          <p
            v-if="!page.props.auth?.user?.verified"
            class="text-sm text-muted-foreground"
          >
            Your email is unverified.
            <Link
              :href="identityEmailVerificationPath()"
              method="post"
              as="button"
              class="underline"
            >
              Resend verification
            </Link>
          </p>

          <div class="space-y-1.5">
            <label for="email-password-challenge" class="text-sm font-medium"
              >Current password</label
            >
            <input
              id="email-password-challenge"
              name="password_challenge"
              type="password"
              autocomplete="current-password"
              class="input-field"
            />
            <p
              v-if="errors.password_challenge"
              class="text-sm text-destructive"
            >
              {{ errors.password_challenge }}
            </p>
          </div>

          <div class="flex items-center gap-3">
            <Button type="submit" :disabled="processing">
              {{ processing ? "Saving..." : "Save" }}
            </Button>
            <p v-if="recentlySuccessful" class="text-sm text-muted-foreground">
              Saved.
            </p>
          </div>
        </div>
      </Form>
    </SettingsLayout>
  </main>
</template>
